/**
 * SmsSubscribeButtonV2
 * Button + modal that lets a spectator opt in to SMS updates for a specific runner (bib).
 * Sends to backend bib-subscriptions endpoint, which records the opt-in and triggers a
 * confirmation SMS via Twilio (using an RD-editable notification rule template).
 */

import { useState } from 'react';
import { subscribeToBib } from '../../lib/bibSubscription';

interface Props {
  slug: string;
  /** When provided, the bib is preset and not user-editable (runner detail page). */
  bib?: string;
  /** Display name shown in the modal header (runner detail page only). */
  runnerName?: string;
}

export function SmsSubscribeButtonV2({ slug, bib, runnerName }: Props) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="v2-sms-cta-button"
        onClick={() => setOpen(true)}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        {bib ? `Get SMS updates for BIB ${bib}` : 'Get SMS updates for a runner'}
      </button>

      {open && (
        <SubscribeModal
          slug={slug}
          presetBib={bib}
          runnerName={runnerName}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

interface ModalProps {
  slug: string;
  presetBib?: string;
  runnerName?: string;
  onClose: () => void;
}

function SubscribeModal({ slug, presetBib, runnerName, onClose }: ModalProps) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [bib, setBib] = useState(presetBib ?? '');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const phoneValid = isPhoneLikelyValid(phone);
  const bibValid = bib.trim().length > 0;
  const nameValid = name.trim().length > 0;
  const canSubmit = !submitting && agreed && phoneValid && bibValid && nameValid;

  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    const result = await subscribeToBib({
      slug,
      bibNumber: bib,
      name,
      phone,
      agreedToTerms: agreed,
    });
    setSubmitting(false);
    if (result.ok) {
      setSuccess(true);
    } else {
      setError(result.error ?? 'Something went wrong.');
    }
  }

  return (
    <>
      <div className="v2-popup-overlay" onClick={onClose} />
      <div className="v2-popup v2-sms-modal" role="dialog" aria-modal="true">
        <div className="v2-sms-modal-header">
          <div>
            <div className="v2-sms-modal-title">SMS updates</div>
            <div className="v2-sms-modal-sub">
              {presetBib
                ? `BIB ${presetBib}${runnerName ? ` · ${runnerName}` : ''}`
                : 'Enter the runner you want to follow'}
            </div>
          </div>
          <button type="button" className="v2-sms-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {success ? (
          <div className="v2-sms-modal-success">
            <p style={{ fontWeight: 600, marginBottom: 6 }}>Almost done — check your phone.</p>
            <p style={{ fontSize: 13, color: 'var(--v2-text-secondary)' }}>
              We just sent a confirmation text. Reply <strong>STOP</strong> at any time to unsubscribe.
            </p>
            <button type="button" className="v2-sms-cta-button" onClick={onClose} style={{ marginTop: 16 }}>
              Done
            </button>
          </div>
        ) : (
          <div className="v2-sms-modal-body">
            <Field label="Your name">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                placeholder="First Last"
              />
            </Field>

            <Field label="Mobile number">
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
                placeholder="(555) 123-4567"
              />
            </Field>

            {!presetBib && (
              <Field label="BIB number">
                <input
                  type="text"
                  value={bib}
                  onChange={(e) => setBib(e.target.value)}
                  placeholder="e.g. 101"
                  inputMode="numeric"
                />
              </Field>
            )}

            <label className="v2-sms-consent">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              <span>
                I agree to receive SMS updates for this runner. Message and data rates may apply.
                Reply <strong>STOP</strong> to unsubscribe, <strong>HELP</strong> for help. See{' '}
                <a href="/sms-terms/" target="_blank" rel="noopener noreferrer">Terms</a>{' '}and{' '}
                <a href="/sms-privacy/" target="_blank" rel="noopener noreferrer">Privacy</a>.
              </span>
            </label>

            {error && <div className="v2-sms-error">{error}</div>}

            <button
              type="button"
              className="v2-sms-cta-button v2-sms-cta-primary"
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              {submitting ? 'Subscribing…' : 'Subscribe'}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="v2-sms-field">
      <span className="v2-sms-field-label">{label}</span>
      {children}
    </label>
  );
}

function isPhoneLikelyValid(input: string): boolean {
  const digits = input.replace(/\D/g, '');
  if (input.startsWith('+')) {
    return digits.length >= 10 && digits.length <= 15;
  }
  return digits.length === 10 || digits.length === 11;
}
